import type { Instance } from "@gadgetinc/mobx-quick-tree";
import { DateTime } from "luxon";
import validate from "validate.js";
import { ShopifyBaseConfig } from "../../connections/shopify/ShopifyBaseConfig";
import { ShopifyPartnerConnectionConfig } from "../../connections/shopify/ShopifyPartnerConnectionConfig";
import { ShopifyTomlConfig } from "../../connections/shopify/ShopifyTomlConfig";
import { RichString } from "../../RichString";
import { WorkspacePageType } from "../../WorkspacePageType";
import { createProblemFinder } from "../createProblemFinder";
import { ProblemSeverity } from "../ProblemSeverity";

validate.validators.againstConnectionApiVersion = (value: string, options: Record<string, any>, _key: any, node: any) => {
  if (!value || !node.shouldShowProblems) return;

  const connectionConfig = node.connectionConfig as Instance<typeof ShopifyPartnerConnectionConfig>;

  if (connectionConfig) {
    const connectionApiVersion = connectionConfig.shopifyApiVersion;
    const tomlApiVersion = value;
    if (connectionApiVersion !== tomlApiVersion) {
      return `API version mismatch. ${RichString.text(tomlApiVersion)} in TOML does not match ${RichString.text(
        connectionApiVersion
      )} in app settings.`;
    }
  }
};

validate.validators.againstConnectionAppUrl = (value: string, options: Record<string, any>, _key: any, node: ShopifyTomlConfig) => {
  // Returns problem message if the app url in the toml doesn't match the app url in the connection
  if (!value || !node.shouldShowProblems) return;

  if (node.connectionConfig) {
    if (!node.clientId) return;

    const connectionConfig = node.connectionConfig as Instance<typeof ShopifyPartnerConnectionConfig>;

    const connectionAppUrl = connectionConfig.getAppConfigByApiKey(node.clientId)?.appURL;
    if (!connectionAppUrl) return;

    if (connectionAppUrl !== value) {
      return `Application URL mismatch. ${RichString.text(connectionAppUrl)} in app settings does not match TOML.`;
    }
  }
};

validate.validators.againstConnectionRedirectUrls = (value: string[], options: Record<string, any>, _key: any, node: ShopifyTomlConfig) => {
  // Returns problem message if there exists redirect url(s) in the connection don't exist in the toml
  if (!node.shouldShowProblems) return;

  const connectionConfig = node.connectionConfig as Instance<typeof ShopifyPartnerConnectionConfig>;
  if (connectionConfig) {
    const connectionRedirectUrls = node.defaultShopifyRedirectUrls;
    const tomlRedirectUrls = value;

    if (!connectionRedirectUrls) return;

    const redirectUrlsDiff = tomlRedirectUrls?.filter((url) => !connectionRedirectUrls.includes(url));

    if (redirectUrlsDiff?.length > 0 || !tomlRedirectUrls?.length) {
      return `Redirect URL mismatch. ${RichString.text(JSON.stringify(connectionRedirectUrls))} in app settings does not match TOML.`;
    }
  }
};

validate.validators.connectionAgainstTomlScopes = (value: string[], options: Record<string, any>, _key: any, node: ShopifyTomlConfig) => {
  // Returns problem message if there exists scope(s) in the connection that don't exist in the toml
  if (!value || !node.shouldShowProblems) return;

  const connectionConfig = node.connectionConfig as Instance<typeof ShopifyPartnerConnectionConfig>;
  if (connectionConfig) {
    const scopes = connectionConfig.requestedScopesAsArray;
    const tomlScopes = node.scopes;

    if (!tomlScopes) return;

    const scopesDiff = scopes.filter((scope) => !tomlScopes.includes(scope));

    if (scopesDiff.length > 0) {
      return `Scope mismatch. ${RichString.text(JSON.stringify(scopesDiff))} in app settings do not exist in TOML.`;
    }
  }
};

export const ShopifyConfigProblems = createProblemFinder<Instance<typeof ShopifyBaseConfig>>(
  {
    apiDeprecationDate: {
      datetime: (_value) => {
        return {
          earliest: DateTime.now(),
          message: `Upgrade Shopify connection API version`,
        };
      },
    },
  },
  {
    name: "Shopify Configuration",
    tabType: WorkspacePageType.ShopifyConnectionsAPIUpgrade,
    fullMessages: false,
  }
);

export const ShopifyTomlConfigProblems = createProblemFinder<Instance<typeof ShopifyTomlConfig>>(
  {
    scopes: {
      connectionAgainstTomlScopes: {
        message: "Connection scopes must match TOML scopes",
        inContextMessage: "^Connection scopes mismatch",
        severity: ProblemSeverity.Error,
      },
    },
    shopifyApiVersion: {
      againstConnectionApiVersion: {
        message: "TOML API version must match connection API version",
        inContextMessage: "^TOML API version mismatch",
        severity: ProblemSeverity.Warning,
      },
    },
    appUrl: {
      againstConnectionAppUrl: {
        message: "TOML app url must match connection app url",
        inContextMessage: "^TOML app url mismatch",
        severity: ProblemSeverity.Error,
      },
    },
    redirectUrls: {
      againstConnectionRedirectUrls: {
        message: "TOML redirect url must match connection redirect url",
        inContextMessage: "^TOML redirect url mismatch",
        severity: ProblemSeverity.Error,
      },
    },
  },
  {
    name: "TOML Configuration",
    fullMessages: false,
    tabType: WorkspacePageType.Code,
    sourceLocation: (node) => node.sourceLocation,
  }
);
